import {_, React, observer, useEffect, classNames, Fragment, AutoSizer, IMaterialTooltipPlacement, Tooltip2, useIntersectionObserver, useAsObservableSource, stringify} from '@api'
import {AssortmentCard, AssortmentRootStore, AssortmentRootStoreContext, LoadingIndicator, ProductLandingComponent, useAssortmentRootStore, useGroupByGroupStore, useStores, useVipLocalStore} from '@modules'
import {icons} from '@stores/ui'
import {PrimaryIconButton} from 'centric-ui-components'
import FlipMove from 'react-flip-move'
import {FixedSizeList, ListChildComponentProps, VariableSizeList} from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import * as css from './AssortmentRoot.css'

/// region Components

interface MyProps {
  className?: string
}

export const AssortmentRoot = observer<MyProps>(function AssortmentRoot({className, ...props}) {
  const groupBy = useGroupByGroupStore(),
    obsProps = useAsObservableSource({groupBy}),
    store = useVipLocalStore((stores) => new AssortmentRootStore(stores, obsProps)),
    {
      catalog, catalog: {strings, flexLayoutCatalogRoot: {setScrollPanel}, assortments: {selectedAssortment}},
      cardsByIndex, onContextMenu, styles, settings: {fluidCards, columnView, hero: {secondLevel}},
      loadMoreAssortments, isAssortmentLoaded, assortmentCount, assortmentCardWidth, showLoader,
      animations, queryTimestamp
    } = store

  useEffect(() => store.registerEffects(), [])

  useEffect(() => {
    console.log(`Mount AssortmentRoot`)
    return () => {
      console.log(`Unmount AssortmentRoot`, {props})
    }
  }, [])

  if (!selectedAssortment) return <div>{strings.intentionallyBlank}</div>

  return (
    <AssortmentRootStoreContext.Provider value={store}>
      <div
        className={classNames(css.root, className)}
        onContextMenu={onContextMenu}
        data-column-view={columnView}
        data-loading={showLoader}
        data-assortments={assortmentCount}
        data-loaded={cardsByIndex.size}
        data-fluid-cards={!columnView && fluidCards}
        data-wrap-second-level-cards={secondLevel.wrap}
        style={styles}
      >
        {showLoader
         ? <LoadingIndicator text='Loading Assortments...'/>
         : assortmentCount === 0 ? <ProductLandingComponent catalog={catalog} />
         : !columnView ?
           <div className={css.cards} ref={setScrollPanel}>
             <FlipMove disableAllAnimations={true} className={css.cardsContainer} {...animations.rootCards} maintainContainerHeight>
               {_.range(0, cardsByIndex.size).map(i => {
                 const bucket = cardsByIndex.get(i)
                 return (
                   <AssortmentCard
                     key={stringify(bucket.key)}
                     className={css.card}
                     bucket={bucket}/>
                 )
               })}
             </FlipMove>
             <LoadMoreAssortmentsButton/>
           </div>
          : <div className={css.cardsAutosize}>
             <AutoSizer>
               {({height, width}) => {
                 return (
                   <InfiniteLoader
                     itemCount={assortmentCount}
                     loadMoreItems={loadMoreAssortments}
                     isItemLoaded={isAssortmentLoaded}
                   >
                     {({onItemsRendered, ref}) => (
                       <FixedSizeList
                         className={css.columnView}
                         itemCount={assortmentCount}
                         width={width}
                         height={height}
                         itemSize={assortmentCardWidth} // Width of an assortment card
                         layout='horizontal'
                         outerRef={setScrollPanel}
                         ref={ref}
                         onItemsRendered={onItemsRendered}
                       >
                         {AssortmentVirtualColumn}
                       </FixedSizeList>)}
                   </InfiniteLoader>)
               }}
             </AutoSizer>
           </div>}
      </div>
    </AssortmentRootStoreContext.Provider>)
})

export const LoadMoreAssortmentsButton = observer(() => {
  const {stores: {strings, config}, loading, hasMore, loadMoreAssortmentsAuto} = useAssortmentRootStore(),
    [intersecting, intersectionRef] = useIntersectionObserver({rootMargin: '100px 0px 0px 0px'})

  useEffect(() => {
    if (!loading && hasMore && intersecting.isIntersecting) {
      !config.isTesting && loadMoreAssortmentsAuto()
    }
  }, [!loading && hasMore && intersecting.isIntersecting])

  // trace()

  return (
    !hasMore ? null : <div className={classNames(css.loadingMore)} ref={intersectionRef}>
      {loading ?
           <Tooltip2 content={strings.loadingMoreItems}>
             {!loading && !intersecting.isIntersecting ? <div/> : <LoadingIndicator />}
           </Tooltip2> :
           <div/>
        }
    </div>)
})


export const AssortmentVirtualColumn = observer<Partial<ListChildComponentProps>>(function AssortmentsCatalogVirtualColumn({index, style, children}) {
  const {cardsByIndex, virtualPageSize} = useAssortmentRootStore(),
    bucket = cardsByIndex.get(index)

  return <div style={style as any}>
    {bucket ? <AssortmentCard inColumnView className={css.card} bucket={bucket} /> : <div className={classNames(css.card, css.noDataLoaded)}></div>}
  </div>
})

export const ToggleAssortmentColumnViewButton = observer<{ tooltipPlacement?: IMaterialTooltipPlacement }>(function ToggleAssortmentColumnViewButton({tooltipPlacement}) {
  const {catalog: {assortments: {selectedAssortment}, settings: {assortment}}} = useStores()

  return (!selectedAssortment ? null : <PrimaryIconButton
    onClick={() => assortment.columnView = !assortment.columnView}
    tooltip='Assortment Column View'
    tooltipPosition='top'
    active={assortment.columnView}
    icon={icons.columnView}/>)
})


/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentRoot.tsx